/**
 * widgets/index.jsx — Frontend widget renderer registry.
 *
 * Each entry maps a server widget `type` → a Solid component that takes
 * { widget, settings, identity } and renders the body.
 *
 * Consolidated widgets map legacy/absorbed slugs directly to their unified
 * parent component so saved user layouts seamlessly render the new UI.
 */

import WelcomeLogin from './WelcomeLogin.jsx';
import ClaimTag from './ClaimTag.jsx';
import Leaderboard from './Leaderboard.jsx';
import PriceCheck from './PriceCheck.jsx';
import MySales from './MySales.jsx';
import SyncPoints from './SyncPoints.jsx';
import GuildStats from './GuildStats.jsx';
import Raffle from './Raffle.jsx';
import FissalOpenWebUI from './FissalOpenWebUI.jsx';
import FissalCost from './FissalCost.jsx';
import LiveFeed from './LiveFeed.jsx';
import Kiosks from './Kiosks.jsx';
import UserDetail from './UserDetail.jsx';
import GuildDetail from './GuildDetail.jsx';
import EsoTimeClock from './EsoTimeClock.jsx';
import Auditing from './Auditing.jsx';
import Settings from './Settings.jsx';
import './Settings.css';
import ToneGenerator from './ToneGenerator.jsx';
import RelayOperations from './RelayOperations.jsx';
import DiscordConsole from './DiscordConsole.jsx';
import ApiDocs from './ApiDocs.jsx';
import Frigate from './Frigate.jsx';
import FamilyDevices from './FamilyDevices.jsx';
import HomelabOverview from './HomelabOverview.jsx';
import OsintResearch from './OsintResearch.jsx';
import UnknownWidget from './UnknownWidget.jsx';
import System from './System.jsx';
import FissalInvestments from './FissalInvestments.jsx';
import DmHoneynet from './DmHoneynet.jsx';
import MarketIntel from './MarketIntel.jsx';

const REGISTRY = {
  // Personal
  'welcome-login':      WelcomeLogin,
  'claim-tag':          ClaimTag,
  'my-sales':           MySales,
  'weekly-sales':       MySales,
  'trader-persona':     MySales,
  'sync-points':        SyncPoints,
  'sync-history':       SyncPoints,
  'settings':           Settings,

  // Guild
  'leaderboard':        Leaderboard,
  'guild-stats':        GuildStats,
  'raffle':             Raffle,
  'my-raffle':          Raffle,
  'admin-raffle':       Raffle,
  'kiosks':             Kiosks,
  'kiosk-tracking':     Kiosks,
  'kiosk-zone-history': Kiosks,
  'trader-bid-history': Kiosks,
  'auditing':           Auditing,
  'guild-tracking':     Auditing,
  'member-auditing':    Auditing,
  'player-tracking':    Auditing,
  'admin-sales':        Auditing,
  'redfur-audit':       Auditing,
  'guild-audit':        Auditing,
  'guild-detail':       GuildDetail,

  // Tools
  'price-check':        PriceCheck,
  'item-detail':        PriceCheck,
  'item-analysis':      PriceCheck,
  'live-feed':          LiveFeed,
  'recent-sales':       LiveFeed,
  'eso-clock':          EsoTimeClock,
  'market-intel':       MarketIntel,
  'user-detail':        UserDetail,
  'player-insight':     UserDetail,
  'profile-management': UserDetail,

  // Family & Homelab
  'homelab-overview':   HomelabOverview,
  'immich':             HomelabOverview,
  'frigate':            Frigate,
  'family-devices':     FamilyDevices,
  'fissal-open-webui':  FissalOpenWebUI,
  'fissal-ai':          FissalOpenWebUI,
  'open-webui':         FissalOpenWebUI,
  'fissal-investments': FissalInvestments,
  'investments':        FissalInvestments,

  // Administration (Technical & System-level)
  'system':             System,
  'server-perf':        System,
  'recent-errors':      System,
  'storage-cleanup':    System,
  'sprint-runner':      System,
  'fissal-prime':       System,
  'admin-tools':        System,
  'fissal-cost':        FissalCost,
  'admin-memories':     FissalCost,
  'relay-operations':   RelayOperations,
  'sync-status':        RelayOperations,
  'tone-generator':     ToneGenerator,
  'admin-tone-generator': ToneGenerator,
  'osint-research':     OsintResearch,
  'horus-tools':        OsintResearch,
  'dm-honeynet':        DmHoneynet,
  'discord-console':    DiscordConsole,
  'api-docs':           ApiDocs,
};

const ALIASES = {
  'guild-audit':          'auditing',
  'redfur-audit':         'auditing',
  'guild-tracking':       'auditing',
  'player-tracking':      'auditing',
  'member-auditing':      'auditing',
  'admin-sales':          'auditing',
  'item-analysis':        'price-check',
  'item-detail':          'price-check',
  'sync-history':         'sync-points',
  'weekly-sales':         'my-sales',
  'trader-persona':       'my-sales',
  'my-raffle':            'raffle',
  'admin-raffle':         'raffle',
  'player-insight':       'user-detail',
  'profile-management':   'user-detail',
  'kiosk-tracking':       'kiosks',
  'kiosk-zone-history':   'kiosks',
  'trader-bid-history':   'kiosks',
  'admin-tone-generator': 'tone-generator',
  'horus-tools':          'osint-research',
  'sync-status':          'relay-operations',
  'admin-tools':          'system',
  'server-perf':          'system',
  'recent-errors':        'system',
  'storage-cleanup':      'system',
  'sprint-runner':        'system',
  'fissal-prime':         'system',
  'admin-memories':       'fissal-cost',
  'recent-sales':         'live-feed',
  'immich':               'homelab-overview',
  'open-webui':           'fissal-open-webui',
};

export function rendererFor(type) {
  return REGISTRY[type] || REGISTRY[ALIASES[type]] || UnknownWidget;
}

export function hasRenderer(type) {
  if (Object.prototype.hasOwnProperty.call(REGISTRY, type)) return true;
  const aliased = ALIASES[type];
  return aliased ? Object.prototype.hasOwnProperty.call(REGISTRY, aliased) : false;
}
